import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { strings } from "@/constants";
import { JobStatusEnum, LanguageEnum } from "@/enums";
import { useExamStatus, useProcessExam } from "@/hooks/exam-results";
import type { ExamRecommendedJobType, ExamStatusResponseType, LanguageType } from "@/types";

interface UsePollingResultInterface {
    code: string,
    jobId: string,
    lang: LanguageType,
    recommendedJobs: ExamRecommendedJobType[],
}

export interface UseExamPollingReturnInterface {
    errorMessage: string,
    onPollExisting: (input: { code: string, jobId: string, lang: LanguageType }) => Promise<void>,
    onProcess: (input: { code: string, lang: LanguageType }) => Promise<void>,
    onReset: () => void,
    progress: number,
    result: UsePollingResultInterface | null,
    status: "error" | "idle" | "loading" | "success",
}

export const useExamPolling = (): UseExamPollingReturnInterface => {
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const isCompletedRef = useRef(false);

    const jobIdRef = useRef<string | null>(null);

    const [status, setStatus] = useState<"error" | "idle" | "loading" | "success">("idle");

    const [progress, setProgress] = useState(0);

    const [errorMessage, setErrorMessage] = useState("");

    const [result, setResult] = useState<UsePollingResultInterface | null>(null);

    const { onRun: runProcessExamHandler } = useProcessExam();

    const { onRun: runExamStatusHandler } = useExamStatus();

    const pollingIntervalMs = 5000;

    const stopPollingHandler = useCallback(
        () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);

                pollingRef.current = null;
            }
        },
        [],
    );

    const buildLangParamHandler = useCallback(
        (lang: LanguageType) => lang === LanguageEnum.AR ? LanguageEnum.AR : undefined,
        [],
    );

    const handleStatusResponseHandler = useCallback(
        (
            statusResponse: ExamStatusResponseType,
            code: string,
            jobId: string,
            lang: LanguageType,
        ): boolean => {
            const {
                displayProgress,
                errorMessage,
                progress,
                result,
                status,
            } = statusResponse;

            const recommendedJobs = result?.recommendedJobs;

            if (isCompletedRef.current) return true;

            setProgress(displayProgress ?? progress);

            if (status === JobStatusEnum.COMPLETED && recommendedJobs) {
                isCompletedRef.current = true;

                stopPollingHandler();

                setProgress(100);

                setStatus("success");

                setResult({
                    code,
                    jobId,
                    lang,
                    recommendedJobs: recommendedJobs,
                });

                return true;
            }

            if (status === JobStatusEnum.FAILED) {
                stopPollingHandler();

                setStatus("error");

                setErrorMessage(errorMessage || strings.errors.tryAgain);

                return true;
            }

            return false;
        },
        [stopPollingHandler],
    );

    const beginPollingHandler = useCallback(
        (
            code: string,
            jobId: string,
            lang: LanguageType,
        ) => {
            const apiLang = buildLangParamHandler(lang);

            const tickHandler = async () => {
                if (!jobIdRef.current || isCompletedRef.current) {
                    stopPollingHandler();

                    return;
                }

                try {
                    const statusResponse = await runExamStatusHandler(
                        jobIdRef.current,
                        apiLang,
                    );

                    handleStatusResponseHandler(
                        statusResponse,
                        code,
                        jobId,
                        lang,
                    );
                } catch (error) {
                    stopPollingHandler();

                    setStatus("error");

                    setErrorMessage(error instanceof Error ? error.message : strings.errors.tryAgain);
                }
            };

            return tickHandler;
        },
        [
            buildLangParamHandler,
            runExamStatusHandler,
            stopPollingHandler,
            handleStatusResponseHandler,
        ],
    );

    const processHandler = useCallback(
        async ({
            code,
            lang,
        }: { code: string, lang: LanguageType }) => {
            jobIdRef.current = null;

            isCompletedRef.current = false;

            setStatus("loading");

            setErrorMessage("");

            setProgress(0);

            setResult(null);

            try {
                const processResponse = await runProcessExamHandler({
                    examCode: code,
                    lang: buildLangParamHandler(lang),
                });

                if (!processResponse.jobId) {
                    setStatus("error");

                    setErrorMessage(strings.errors.invalidExamCode);

                    return;
                }

                jobIdRef.current = processResponse.jobId;

                const tickHandler = beginPollingHandler(
                    code,
                    processResponse.jobId,
                    lang,
                );

                await tickHandler();

                if (jobIdRef.current && !isCompletedRef.current) {
                    pollingRef.current = setInterval(
                        tickHandler,
                        pollingIntervalMs,
                    );
                }
            } catch (err) {
                setStatus("error");

                setErrorMessage(err instanceof Error ? err.message : strings.errors.tryAgain);
            }
        },
        [
            runProcessExamHandler,
            buildLangParamHandler,
            beginPollingHandler,
        ],
    );

    const pollExistingHandler = useCallback(
        async ({
            code,
            jobId,
            lang,
        }: { code: string, jobId: string, lang: LanguageType }) => {
            jobIdRef.current = jobId;

            isCompletedRef.current = false;

            setStatus("loading");

            setErrorMessage("");

            setProgress(0);

            const tickHandler = beginPollingHandler(
                code,
                jobId,
                lang,
            );

            await tickHandler();

            if (jobIdRef.current && !isCompletedRef.current) {
                pollingRef.current = setInterval(
                    tickHandler,
                    pollingIntervalMs,
                );
            }
        },
        [beginPollingHandler],
    );

    const resetHandler = useCallback(
        () => {
            stopPollingHandler();

            jobIdRef.current = null;

            isCompletedRef.current = false;

            setStatus("idle");

            setErrorMessage("");

            setProgress(0);

            setResult(null);
        },
        [stopPollingHandler],
    );

    useEffect(
        () => () => stopPollingHandler(),
        [stopPollingHandler],
    );

    return {
        errorMessage,
        onPollExisting: pollExistingHandler,
        onProcess: processHandler,
        onReset: resetHandler,
        progress,
        result,
        status,
    };
};
